import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { SwipeableDrawer } from '@mui/material'
import { useGetAccountsQuery } from 'services/accounts'
import { useGetSettingsQuery } from 'services/settings'
import PrintCheckBladeForm from 'components/containers/bills/components/PrintCheckBladeForm'
import WizardStepper from 'components/generic/stepper/WizardStepper'
import Loading from 'components/generic/Loading'
import { incrementCheckNumber } from 'components/containers/inbound-tickets/utils'
import { ACCOUNT_PAYMENT_TYPE_ENUMS } from 'utils/constants'
import { locationIsNotEmpty } from 'utils/helpers'

const PAYFROM_OPTIONS = [{ id: 'Main Checking Account', name: 'Main Checking Account' }]

const addressFromContact = contact =>
  contact?.address1
    ? {
        address1: contact?.address1,
        address2: contact?.address2,
        city: contact?.city,
        state: contact?.state,
        zip: contact?.zip,
      }
    : null

const PrintCheckBlade = ({
  open,
  setOpen,
  checkPaymentsData,
  selectedTicketsData,
  customersData,
  handleSubmit,
  setValue,
  watch,
  trigger,
  control,
  reset,
  errors,
  isValid,
  onPrint,
  isLoadingPaymentsData,
  pricePrepend,
  isBatchBill,
  startingCheckNumberOverrides,
}) => {
  const inboundTicketLocationId = selectedTicketsData?.[0]?.[0]?.locationId

  const { yardId } = useSelector(state => state.auth)

  // note: the argument "origin" is used so that this GET does not invalidate the redux settings (which could cause the page to rerender)
  const { data: settings } = useGetSettingsQuery(
    { origin: 'PrintCheckBlade' },
    { skip: !open || !yardId, refetchOnMountOrArgChange: true },
  )

  const { startingCheckNumber, automaticallyIncrementCheckNumber } = settings?.purchaseSettings?.payment || {}

  const [accountOptions, setAccountOptions] = useState([])
  const [supplierContactOptionsList, setSupplierContactOptionsList] = useState([[]])
  const [supplierContactDict, setSupplierContactDict] = useState({})
  const [supplierLocationOptionsList, setSupplierLocationOptionsList] = useState([[]])
  const [supplierLocationDict, setSupplierLocationDict] = useState({})

  const { data: accountsData, isLoading: isLoadingAccountsData } = useGetAccountsQuery({})

  const isLoading = isLoadingAccountsData || isLoadingPaymentsData

  useEffect(() => {
    const checkingAccounts = accountsData?.Items?.filter(item => item.type === ACCOUNT_PAYMENT_TYPE_ENUMS.CHECKING)
    if (checkingAccounts?.length > 0) {
      setAccountOptions(
        checkingAccounts.map(item => ({
          id: item.id,
          name: item.accountName,
        })),
      )
    } else {
      setAccountOptions(PAYFROM_OPTIONS)
    }
  }, [accountsData])

  const updateLocation = (_, location, paymentId) => {
    setValue(`${paymentId}.information.location`, location)
    setSupplierLocationDict(prevState => ({ ...prevState, [paymentId]: location }))
  }

  const updateContact = (_, contact, paymentId) => {
    const customersAndTicketsIndex = isBatchBill ? checkPaymentsData?.findIndex(payment => payment.id === paymentId) : 0
    setValue(`${paymentId}.information.payToTheOrder`, contact)
    setSupplierContactDict(prevState => ({ ...prevState, [paymentId]: contact }))
    if (contact?.address1) {
      const contactAddress = addressFromContact(contact)
      setSupplierLocationOptionsList(prevLocations => {
        const newLocations = [...prevLocations]
        newLocations[customersAndTicketsIndex] = [
          contactAddress,
          ...prevLocations[customersAndTicketsIndex].filter(({ id }) => !!id),
        ]
        return newLocations
      })
      updateLocation(null, contactAddress, paymentId)
    } else {
      const newLocation = supplierLocationDict?.[paymentId]?.id
        ? // if the user previously selected a customer location (not the contact location), then use it
          supplierLocationDict?.[paymentId]
        : // otherwise, try to use the ibt location. If no ibt location, then use the first customer location
          supplierLocationOptionsList?.[customersAndTicketsIndex]?.find(
            location => location?.id === inboundTicketLocationId,
          ) || supplierLocationOptionsList?.[customersAndTicketsIndex]?.[1]
      if (newLocation) {
        setSupplierLocationOptionsList(prevLocations => {
          const newLocations = [...prevLocations]
          newLocations[customersAndTicketsIndex] = prevLocations[customersAndTicketsIndex].filter(({ id }) => !!id)
          return newLocations
        })
        updateLocation(null, newLocation, paymentId)
      }
    }
  }

  useEffect(() => {
    if (open && customersData?.[0]?.displayName) {
      const resetPayload = {}
      const newSupplierContactOptionsList = []
      const newSupplierLocationOptionsList = []

      checkPaymentsData.forEach((payment, index) => {
        const customersAndTicketsIndex = isBatchBill ? index : 0

        const validCustomerLocations =
          customersData?.[customersAndTicketsIndex]?.locations?.filter(location => locationIsNotEmpty(location)) || []

        const companyName = { firstName: customersData?.[customersAndTicketsIndex]?.displayName, lastName: '' }
        const defaultMemo =
          selectedTicketsData?.[customersAndTicketsIndex]?.length > 0
            ? `Ticket${selectedTicketsData[customersAndTicketsIndex].length === 1 ? '' : 's'} #${selectedTicketsData[customersAndTicketsIndex].map(ticket => ticket.ticketId).join(', ')}`
            : ''
        const defaultPayToContact = customersData?.[customersAndTicketsIndex]?.contacts?.find(
          contact => !!contact.isDefaultPayToContact,
        )
        const defaultPayToLocation = validCustomerLocations?.find(location => !!location.isDefaultPayToLocation)
        const inboundTicketLocationId = selectedTicketsData?.[customersAndTicketsIndex]?.[0]?.locationId

        let checkNumber

        if (isBatchBill) {
          checkNumber = startingCheckNumberOverrides[customersAndTicketsIndex]
        } else if (automaticallyIncrementCheckNumber) {
          checkNumber = incrementCheckNumber(startingCheckNumber, index)
        } else {
          checkNumber = ''
        }

        resetPayload[payment.id] = {
          details: {
            payFrom: accountOptions[0]?.id || '',
            checkAmount: payment?.total || 0,
            checkNumber,
            useAutoincrementedCheckNumber: automaticallyIncrementCheckNumber,
          },
          information: {
            payToTheOrder:
              defaultPayToContact ||
              companyName ||
              supplierContactOptionsList[customersAndTicketsIndex][0] ||
              customersData?.[customersAndTicketsIndex]?.contacts?.[0],
            location: defaultPayToLocation || validCustomerLocations[0],
            memo: defaultMemo,
          },
        }
        newSupplierContactOptionsList.push([
          companyName,
          ...(customersData?.[customersAndTicketsIndex]?.contacts || []),
        ])
        let newLocation
        if (validCustomerLocations.length > 0) {
          const firstLocation = validCustomerLocations[0]
          const ibtLocation = validCustomerLocations.find(location => location.id === inboundTicketLocationId)
          newSupplierLocationOptionsList.push(validCustomerLocations)
          newLocation = defaultPayToLocation || ibtLocation || firstLocation
        } else {
          const firstContactLocation = addressFromContact(
            defaultPayToContact || customersData?.[customersAndTicketsIndex]?.contacts?.[0],
          )
          newSupplierLocationOptionsList.push([firstContactLocation])
          newLocation = firstContactLocation
        }
        updateContact(null, defaultPayToContact || companyName, payment.id)
        updateLocation(null, newLocation, payment.id)
      })
      reset(resetPayload)

      setSupplierContactOptionsList(newSupplierContactOptionsList)
      setSupplierLocationOptionsList(newSupplierLocationOptionsList)
    }
  }, [checkPaymentsData, customersData, settings, selectedTicketsData, open])

  const handleValidatePartial = async activeStep => {
    const paymentId = checkPaymentsData?.[activeStep]?.id
    const result = await trigger([
      `${paymentId}.details.payFrom`,
      `${paymentId}.details.checkAmount`,
      `${paymentId}.details.checkNumber`,
      `${paymentId}.details.useAutoincrementedCheckNumber`,
      `${paymentId}.information.account`,
      `${paymentId}.information.payToTheOrder`,
      `${paymentId}.information.location`,
    ])
    return result
  }

  const onSave = async () => {
    onPrint()
  }

  const handleSubmitPartial = async () => {
    if (isValid) onPrint()
  }

  const steps = checkPaymentsData?.map((payment, index) => {
    const customersAndTicketsIndex = isBatchBill ? index : 0
    const buttonTitles = []
    buttonTitles.push(index === checkPaymentsData?.length - 1 ? 'PRINT CHECKS' : 'NEXT')
    buttonTitles.push(index === 0 ? 'CANCEL' : 'BACK')
    return {
      label: isBatchBill ? customersData?.[customersAndTicketsIndex]?.displayName : `Check ${index + 1}`,
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: buttonTitles,
      contentComponent: (
        <PrintCheckBladeForm
          key={payment.id}
          payment={payment}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          accountOptions={accountOptions}
          defaultMemo={
            selectedTicketsData?.[customersAndTicketsIndex]?.length > 0
              ? `Ticket${selectedTicketsData[customersAndTicketsIndex].length === 1 ? '' : 's'} #${selectedTicketsData[customersAndTicketsIndex].map(ticket => ticket.ticketId).join(', ')}`
              : ''
          }
          customerData={customersData?.[customersAndTicketsIndex]}
          automaticallyIncrementCheckNumber={automaticallyIncrementCheckNumber}
          checkNumber={
            isBatchBill
              ? startingCheckNumberOverrides[customersAndTicketsIndex]
              : incrementCheckNumber(startingCheckNumber, index)
          }
          supplierContactOptions={supplierContactOptionsList[customersAndTicketsIndex]}
          supplierLocationOptions={supplierLocationOptionsList[customersAndTicketsIndex]}
          supplierContact={supplierContactDict[payment.id]}
          supplierLocation={supplierLocationDict[payment.id]}
          updateContact={updateContact}
          updateLocation={updateLocation}
          pricePrepend={pricePrepend}
          isBatchBill={isBatchBill}
        />
      ),
    }
  })

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      PaperProps={{ sx: { maxWidth: '1024px', width: '100%' } }}
      disableSwipeToOpen
    >
      <form onSubmit={handleSubmit(onSave)}>
        {isLoading && <Loading />}
        {checkPaymentsData?.length > 0 && (
          <WizardStepper
            steps={steps}
            isWide
            title={`Print Check${checkPaymentsData?.length > 1 ? 's' : ''}`}
            validationCheck
            onHideModal={() => setOpen(false)}
            onSubmitPartial={handleSubmitPartial}
            onValidatePartial={handleValidatePartial}
            disableAlertModal
          />
        )}
      </form>
    </SwipeableDrawer>
  )
}

PrintCheckBlade.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  checkPaymentsData: PropTypes.array,
  selectedTicketsData: PropTypes.array,
  customersData: PropTypes.array,
  handleSubmit: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
  trigger: PropTypes.func,
  control: PropTypes.object,
  reset: PropTypes.func,
  errors: PropTypes.object,
  isValid: PropTypes.bool,
  onPrint: PropTypes.func,
  isLoadingPaymentsData: PropTypes.bool,
  pricePrepend: PropTypes.string,
  isBatchBill: PropTypes.bool,
  startingCheckNumberOverrides: PropTypes.array,
}

PrintCheckBlade.defaultProps = {
  open: false,
  setOpen: () => {},
  checkPaymentsData: [],
  selectedTicketsData: [],
  customersData: [],
  handleSubmit: () => {},
  setValue: () => {},
  watch: () => {},
  trigger: () => {},
  control: {},
  reset: () => {},
  errors: {},
  isValid: true,
  onPrint: () => {},
  isLoadingPaymentsData: false,
  pricePrepend: '$',
  isBatchBill: false,
  startingCheckNumberOverrides: [],
}

export default PrintCheckBlade
